import { ListFilter, Plus } from "lucide-react";
import { useMemo, useState, type FormEvent } from "react";

import { CollectionSearch } from "../components/CollectionSearch";
import { CollectionView } from "../components/CollectionView";
import { EmptyState } from "../components/EmptyState";
import { ViewModeToggle } from "../components/ViewModeToggle";
import { matchesCollectionSearch } from "../lib/collection-search";
import { useSectionSearch } from "../lib/section-search";
import { interventionColumns, interventionTitleLabel, toInterventionRecord } from "../lib/operational-collection-records";
import type { UserSession } from "../lib/trpc";
import { trpc } from "../lib/trpc";
import { useViewMode } from "../lib/view-mode";

type StatusFilter = "all" | "scheduled" | "in_progress" | "completed";

/** Le voci del filtro, scritte una volta sola: la barra e il foglio stampato dicono lo stesso. */
const statusFilterLabels: Record<StatusFilter, string> = {
  all: "Tutti",
  scheduled: "Programmati",
  in_progress: "In corso",
  completed: "Completati",
};

function nextHourValue() {
  const date = new Date(Date.now() + 60 * 60 * 1000);
  date.setMinutes(0, 0, 0);
  return new Date(date.getTime() - date.getTimezoneOffset() * 60_000).toISOString().slice(0, 16);
}

export function InterventionsPage({ session }: { session: UserSession }) {
  const canManage = session.membership.role !== "collaborator";
  const utils = trpc.useUtils();
  const interventions = trpc.interventions.list.useQuery();
  const sites = trpc.sites.list.useQuery();
  const team = trpc.team.list.useQuery(undefined, { enabled: canManage });
  const catalog = trpc.catalog.list.useQuery(undefined, { enabled: canManage });
  const create = trpc.interventions.schedule.useMutation({ onSuccess: () => void utils.interventions.list.invalidate() });
  const [open, setOpen] = useState(false);
  const [filter, setFilter] = useState<StatusFilter>("all");
  const [viewMode, setViewMode] = useViewMode("interventi");
  const [search, setSearch] = useSectionSearch();
  const records = useMemo(() => (interventions.data ?? []).map(toInterventionRecord), [interventions.data]);
  const filteredRecords = useMemo(
    () => records.filter((record, index) => {
      const intervention = interventions.data?.[index];
      return (filter === "all" || intervention?.status === filter)
        && matchesCollectionSearch(search, [record.title, ...Object.values(record.cells).map((cell) => cell?.text)]);
    }),
    [filter, interventions.data, records, search],
  );

  async function submit(event: FormEvent<HTMLFormElement>) {
    event.preventDefault();
    const data = new FormData(event.currentTarget);
    const assigneeIds = data.getAll("assigneeIds").map(String);
    const jobTypeId = String(data.get("jobTypeId"));
    const selectedJob = catalog.data?.flatMap((category) => category.jobTypes).find((job) => job.id === jobTypeId);
    const label = selectedJob?.name || String(data.get("customWork"));
    await create.mutateAsync({
      siteId: String(data.get("siteId")), title: String(data.get("title")), description: String(data.get("description") || "") || null,
      scheduledStart: new Date(String(data.get("scheduledStart"))), scheduledEnd: null, priority: Number(data.get("priority")),
      assigneeIds, activities: [{ jobTypeId: selectedJob?.id ?? null, label }], confirmSiteAssignments: true,
    });
    setOpen(false);
  }

  return (
    <main className="page">
      <header className="page-heading">
        <div><p className="eyebrow">Agenda operativa</p><h1>Interventi</h1><p>Ogni lavoro ha luogo, attività, persone e tempo registrato.</p></div>
        <div className="page-heading__actions">
          <CollectionSearch label="Cerca interventi" value={search} onChange={setSearch} placeholder="Titolo, struttura o collaboratore" />
          <ViewModeToggle value={viewMode} onChange={setViewMode} />
          {canManage && <button className="button button--primary" onClick={() => setOpen(!open)}><Plus size={19} /> Programma lavoro</button>}
        </div>
      </header>

      {open && <form className="editor-card" onSubmit={submit}>
        <div className="editor-card__heading"><h2>Programma un intervento</h2><p>Assegna lavoro e persone. MANUTIO collegherà anche la struttura se necessario.</p></div>
        <div className="form-grid">
          <label><span>Titolo</span><input name="title" required minLength={2} placeholder="Manutenzione settimanale" /></label>
          <label><span>Struttura</span><select name="siteId" required>{sites.data?.map((site) => <option key={site.id} value={site.id}>{site.name}</option>)}</select></label>
          <label><span>Data e ora</span><input name="scheduledStart" type="datetime-local" defaultValue={nextHourValue()} required /></label>
          <label><span>Priorità</span><select name="priority" defaultValue="2"><option value="1">Alta</option><option value="2">Normale</option><option value="3">Bassa</option></select></label>
          <label><span>Tipo di lavoro</span><select name="jobTypeId" defaultValue=""> <option value="">Attività personalizzata</option>{catalog.data?.map((category) => <optgroup key={category.id} label={category.name}>{category.jobTypes.filter((job) => job.active).map((job) => <option key={job.id} value={job.id}>{job.name}</option>)}</optgroup>)}</select></label>
          <label><span>Lavoro personalizzato</span><input name="customWork" placeholder="Usato se non scegli dal catalogo" /></label>
          <fieldset className="form-grid__wide assignee-field"><legend>Collaboratori</legend><div>{team.data?.filter((member) => member.active).map((member) => <label className="check-control" key={member.userId}><input type="checkbox" name="assigneeIds" value={member.userId} defaultChecked={member.role === "collaborator"} /> {member.name}</label>)}</div></fieldset>
          <label className="form-grid__wide"><span>Note</span><textarea name="description" rows={3} placeholder="Istruzioni, accessi, attenzioni particolari" /></label>
        </div>
        {create.error && <p className="form-error">{create.error.message}</p>}
        <div className="form-actions"><button className="button button--ghost" type="button" onClick={() => setOpen(false)}>Annulla</button><button className="button button--primary" disabled={create.isPending}>Programma intervento</button></div>
      </form>}

      <div className="filter-bar"><span><ListFilter size={18} /> Mostra</span>{(["all", "scheduled", "in_progress", "completed"] as const).map((value) => <button type="button" className={filter === value ? "active" : ""} onClick={() => setFilter(value)} key={value}>{statusFilterLabels[value]}</button>)}</div>
      <section className="collection-section" data-view={viewMode}>
        {interventions.isLoading && <p className="loading-copy">Caricamento interventi...</p>}
        {!interventions.isLoading && interventions.isError && <EmptyState title="Impossibile caricare gli interventi"><button type="button" className="button button--outline" onClick={() => void interventions.refetch()}>Riprova</button></EmptyState>}
        {!interventions.isLoading && !interventions.isError && records.length === 0 && <EmptyState title="Nessun intervento">Programma il prossimo lavoro.</EmptyState>}
        {!interventions.isLoading && !interventions.isError && records.length > 0 && filteredRecords.length === 0 && <EmptyState title="Nessun risultato">Prova a modificare la ricerca o il filtro.</EmptyState>}
        {!interventions.isLoading && !interventions.isError && filteredRecords.length > 0 && <CollectionView
          mode={viewMode}
          ariaLabel="Interventi"
          titleLabel={interventionTitleLabel}
          columns={interventionColumns}
          records={filteredRecords}
          print={{
            section: "Interventi",
            details: [filter === "all" ? null : `Stato: ${statusFilterLabels[filter]}`, search ? `Ricerca: ${search}` : null],
          }}
        />}
      </section>
    </main>
  );
}
